import { useMemo } from "react";

export type Resolver<T> = (source: T) => any;

export type FieldConfig<T> = {
  key: string;
  resolver: Resolver<T>;
};

export type SectionStatus = {
  key: string;
  completedFields: number;
  totalFields: number;
  isComplete: boolean;
  missingFields: string[];
  weight: number;
};

export type ProfileCompletenessResult = {
  percentage: number;
  sections: SectionStatus[];
  firstIncompleteSection?: SectionStatus;
};

export type SectionConfig<T> = {
  key: string;
  weight: number;
  label: string;
  fields: FieldConfig<T>[];
};

const calculateWeightedPercentage = (
  sections: {
    weight: number;
    completedFields: number;
    totalFields: number;
  }[],
) => {
  const totalWeight = sections?.reduce((sum, s) => sum + s?.weight, 0);

  const earnedWeight = sections?.reduce((sum, section) => {
    const sectionCompletion =
      section?.totalFields === 0
        ? 0
        : section?.completedFields / section?.totalFields;

    return sum + sectionCompletion * section?.weight;
  }, 0);

  return Math.round((earnedWeight / totalWeight) * 100);
};

export function useProfileCompleteness<T>(
  source: T,
  sections: SectionConfig<T>[],
) {
  const sectionsStatus = useMemo(
    () =>
      sections?.map((section) => {
        const totalFields = section.fields.length;

        const missingFields = section?.fields
          ?.filter((field) => !field.resolver(source))
          ?.map((field) => field.key);

        const completedFields = totalFields - missingFields.length;

        return {
          key: section.key,
          label: section.label,
          totalFields,
          completedFields,
          missingFields,
          isComplete: missingFields.length === 0,
          weight: section.weight,
        };
      }),
    [sections, source],
  );

  const percentage = useMemo(
    () => calculateWeightedPercentage(sectionsStatus),
    [sectionsStatus],
  );

  const firstInCompleteSection = useMemo(
    () => sectionsStatus?.find((s) => !s.isComplete),
    [sectionsStatus],
  );

  return {
    percentage,
    sections: sectionsStatus,
    firstInCompleteSection: firstInCompleteSection,
  };
}
